import { useEffect } from 'react';
import { useToast } from '@/hooks';
import { useEventsContext } from '@/providers/Events';
import { useQuestionnaireContext, useQuestionnaireFormContext } from '@/components/Questionnaire/QuestionnaireProvider';
import * as QuestionnaireApiModels from '@/typings/questionnaires-api-models';
import * as tsClient from '@tsClient';
import useNoodleApi from '@/hooks/useNoodleApi';

type Props = {
  formData: {
    id: string;
  }
};

const HandleQuestionnaireActions: React.FC<Props> = ({ formData }) => {
  const toast = useToast();
  const {
    userWorkflowId,
  } = useQuestionnaireContext();
  const {
    formContextId,
    setValue,
  } = useQuestionnaireFormContext();
  const { addListener, removeListener } = useEventsContext();

  const { getData: getAutofillInitialValueFn } = useNoodleApi(tsClient.formRequests.getAutofillInitialValue);

  const fetchAndPopulateData = async ({
    referenceId, referenceType, source, successMessage,
  } : {
    referenceId: string | null,
    referenceType: string,
    source: QuestionnaireApiModels.QuestionnaireResponse['source'],
    successMessage: string,
  }): Promise<void> => {
    const params={
      questionnaireId: formData.id,
      referenceId,
      referenceType,
      userWorkflowId: userWorkflowId ?? '',
    };
    const { data } = await getAutofillInitialValueFn(params);
    if (!data) return;

    toast(useToast.ToastTypeVariants.SUCCESS, successMessage);

    Object.entries(data).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((item, index) => {
          if (typeof item === 'object' && item !== null) {
            Object.entries(item).forEach(([innerKey, innerValue]) => {
              const fieldKey = `${key}[${index}].${innerKey}`;
              setValue({ hasAutofillRun: true, key: fieldKey, source, value: innerValue });
            });
          }
        });
      } else if (typeof value === 'object' && value !== null) {
        Object.entries(value).forEach(([innerKey, innerValue]) => {
          const fieldKey = `${key}.${innerKey}`;
          setValue({ hasAutofillRun: true, key: fieldKey, source, value: innerValue});
        });
      } else {
        setValue({ hasAutofillRun: true, key, source, value });
      }
    });
  };

  useEffect(() => {
    const listener = addListener('populateCreditReportData', async ({ contextId }) => {
      try {
        if (formContextId !== contextId) return;
        await fetchAndPopulateData({
          referenceId: 'COMBO',
          referenceType: 'credit-report',
          source: 'credit-report',
          successMessage: 'Populating credit report data. Please wait a few seconds.',
        });
      } catch (error) {
        toast(useToast.ToastTypeVariants.ERROR, 'Credit report import failed!');
      }
    });
    return () => removeListener('populateCreditReportData', listener);
  }, [addListener, removeListener]);

  useEffect(() => {
    const listener = addListener('populatePayOrganizerData', async ({ contextId }) => {
      try {
        if (formContextId !== contextId) return;
        await fetchAndPopulateData({
          referenceId: null,
          referenceType: 'document-request-internal-data',
          source: 'initial-values',
          successMessage: 'Populating pay organizer data. Please wait a few seconds.',
        });
      } catch (error) {
        toast(useToast.ToastTypeVariants.ERROR, 'Pay organizer import failed!');
      }
    });
    return () => removeListener('populatePayOrganizerData', listener);
  }, [addListener, removeListener]);

  return null;
};

export default HandleQuestionnaireActions;